/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  type BreadcrumbV2Model,
  type GetProductShelfACESResultsParamsModel,
  type ProductShelfACESResponseModel,
} from '@/api/types/browse-search-types';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { type ProductShelfACESResponse } from '../interface';
import { type AxiosInstance, isAxiosError } from 'axios';
import { type QueryClient } from '@tanstack/react-query';
import { getAxios } from '@/lib/axios';
import { parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { pageTypes } from '@/constants/page';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useAppState } from '@/stores/AppState';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { usePageType } from './getPageType';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useContentStackPLPData } from '@/features/productListingPage/api/getContentStackProductListingPageData';
import logger from '@/utils/logger';
import { useMemo } from 'react';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import {
  getExperimentId,
  getSearchExperimentConfig,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useRetryConfigForCategory } from '@/components/RetryConfigContext';
import { useVertexPersonalizationParams } from '@/features/search/hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { isServer } from '@/utils/isServer';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import { useRouter } from 'next/navigation';
import environmentConfig from '@/config/environment';
import { useDeviceType } from '@/utils/useDeviceType';
import { getShelfRequestStoreId } from '@/utils/getShelfRequestStoreId';

const URL = '/sls/b2c/product-discovery-browse-search-data/v2/product-shelves';
const BR_URL = '/sls/pd/product-browse-search/v1/product-shelves';

type Options = {
  locale?: Locale;
  partGroupId: string;
  taxonomyPath?: string;
  tag?: string;
  canonicalPath?: string;
  pageType?: string;
  botEnabledFacetPath?: string;
  path?: string | undefined;
  recsPerPage?: string | undefined;
  makeModelYearPath?: string | undefined;
  storeId?: string | undefined;
  preview: boolean;
  ignoreVehicleSpecificProductsCheck?: boolean;
  rewardsId: string;
  eCookieId: string;
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  vehicleQuestions?: string;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
};

const getProductShelfACESResults = async (
  options: Options,
  pageNumber: number,
  axiosInstance?: AxiosInstance,
) => {
  const {
    eCookieId,
    locale,
    canonicalPath,
    partGroupId,
    pageType,
    preview,
    path,
    recsPerPage,
    botEnabledFacetPath,
    makeModelYearPath,
    rewardsId,
    tag,
    storeId,
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
    ignoreVehicleSpecificProductsCheck,
    experimentId,
  } = options;

  if (!canonicalPath) {
    throw new Error('canonicalPath must be provided in getCategoryShelfResults');
  }

  if (pageType !== pageTypes.ProductShelf && !tag) {
    throw new Error('No product shelf data on categories pages');
  }

  const country = getCountryFromLocale(locale || 'en-US');
  const parsedUrl = parseUrl(path ?? '');

  let sortValue = parsedUrl.query.sort;
  if (sortValue === 'best-seller-asc' || sortValue === 'best-seller-desc') {
    sortValue = 'best-sellers';
  } else if (sortValue === 'recently-added-desc') {
    sortValue = 'recently-added';
  } else if (sortValue === 'customer-rating-desc') {
    sortValue = 'customer-rating';
  }

  const shouldAddIgnoreVehicleParam =
    pageType === pageTypes.ProductShelf &&
    (!!makeId || !!modelId || !!vehicleTypeId) &&
    ignoreVehicleSpecificProductsCheck === undefined;

  const ignoreVehicleSpecificProductsCheckOverride = shouldAddIgnoreVehicleParam
    ? false
    : ignoreVehicleSpecificProductsCheck;

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year,
    makeId,
    modelId,
    vehicleTypeId,
    vehicleQuestions,
  });

  const params: GetProductShelfACESResultsParamsModel = {
    country,
    partGroupId,
    preview,
    pageNumber,
    recordsPerPage: recsPerPage
      ? Number(recsPerPage)
      : parsedUrl.query.recsPerPage
        ? Number(parsedUrl.query.recsPerPage)
        : undefined,
    sort: sortValue,
    storeId,
    ruleTags: tag,
    facet: parsedUrl.query.facet ?? undefined,
    minPrice: parsedUrl.query.minPrice ? Number(parsedUrl.query.minPrice) : undefined,
    maxPrice: parsedUrl.query.maxPrice ? Number(parsedUrl.query.maxPrice) : undefined,
    botEnabledFacetPath,
    searchedKeyword: !parsedUrl.query.redirectUrl
      ? (parsedUrl.query.filterByKeyWord ?? parsedUrl.query.searchText)
      : undefined,
    makeModelYearPath: makeModelYearPath === '' ? undefined : makeModelYearPath,
    partNumberSearch: Boolean(parsedUrl.query.partNumberSearch),
    ...(ignoreVehicleSpecificProductsCheckOverride !== undefined && {
      ignoreVehicleSpecificProductsCheck: ignoreVehicleSpecificProductsCheckOverride,
    }),
    ...(country === 'BRA' && {
      salesChannel: 'ECOMM',
      customerType: 'B2C',
    }),
    ...validatedVehicleParams,
  };

  const normalizedExperimentId = getExperimentId(experimentId, true);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: true, // Search experiment is always enabled for product shelves
  });

  // Hash userId if provided (for both client and server-side calls)
  const hashedUserId = options.userId ? await getSHA256(options.userId) : '';
  const enhancedParams = buildSearchExperimentParams(searchExpConfig, params, {
    visitorId: options.visitorId,
    userId: hashedUserId,
  });
  const acesHeaders = buildSearchExperimentHeaders(searchExpConfig);
  const finalUrl = country === 'BRA' ? BR_URL : URL;

  const response = await getAxios(axiosInstance).get<ProductShelfACESResponseModel>(
    `${requestBaseURLGCPExtOrInt}${finalUrl}`,
    {
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
      headers: {
        ...acesHeaders,
        eCookieId,
        ...(!!rewardsId && { rewardsId }),
      },
      params: enhancedParams,
    },
  );
  return productShelfResultsSelector(response.data);
};

const productShelfResultsSelector = ({
  attributionToken,
  filter,
  productShelfResults,
  xFusionQueryId,
}: ProductShelfACESResponseModel): ProductShelfACESResponse => {
  if (!productShelfResults) {
    return {
      attributionToken,
      filter,
      productShelfResults,
      xFusionQueryId,
    };
  }

  // Map the breadcrumbs to maintain backward compatibility with old field names
  const mappedBreadCrumbs = productShelfResults.breadCrumbs?.map((breadcrumb) => {
    return {
      taxonomyId: breadcrumb.categoryIdPath,
      displayName: breadcrumb.categoryName,
      taxonomyPath: breadcrumb.taxonomyPath,
      pageType: breadcrumb.pageType,
      canonicalPath: breadcrumb.canonicalUrl,
    };
  });
  return {
    attributionToken,
    filter,
    productShelfResults: {
      ...productShelfResults,
      breadCrumbs: mappedBreadCrumbs as BreadcrumbV2Model[],
    },
    xFusionQueryId,
  };
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<ProductShelfACESResponse, number> = (
  data,
) => {
  const mergedData = data?.pages?.reduce(
    (acc: ProductShelfACESResponse, page: ProductShelfACESResponse) => {
      const { attributionToken, filter, xFusionQueryId } = page;
      if (page?.productShelfResults) {
        return {
          ...acc,
          productShelfResults: {
            ...acc.productShelfResults,
            ...page.productShelfResults,
            skuRecords: [
              ...(acc.productShelfResults?.skuRecords ?? []),
              ...(page.productShelfResults?.skuRecords ?? []),
            ],
          },
          attributionToken,
          filter,
          xFusionQueryId,
        };
      }
      return acc;
    },
    { productShelfResults: { skuRecords: [] } } as unknown as ProductShelfACESResponse,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useACESProductShelfResultsData,
  query: productsShelfACESQuery,
  prefetch: prefetchACESProductShelfResultsData,
  getFromCache,
} = createInfiniteQuery<
  ProductShelfACESResponse,
  Options,
  number,
  BaseMergedData<ProductShelfACESResponse, number>
>(['productshelf-results'], getProductShelfACESResults, {
  initialPageParam: 1,
  getNextPageParam: (lastPage, allPages) => {
    const totalRecords = lastPage?.productShelfResults?.totalNumberOfRecords ?? 0;
    const currentPageNumber = allPages.length;

    // Count actual records loaded across all pages
    const loadedRecords = allPages.reduce((sum, page) => {
      return sum + (page.productShelfResults?.skuRecords?.length ?? 0);
    }, 0);

    // Only fetch next page if we haven't loaded all records yet
    if (loadedRecords < totalRecords) {
      return currentPageNumber + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

export const useProductShelfACESResults = ({
  enabled = true,
  ...rest
}: {
  enabled?: boolean;
} = {}) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const headerResult = useHeaderData();
  const isBot = useDeviceType() === 'bot';
  const preferredVehicle = getPreferredVehicle(headerResult.data);
  const locale = useLocale();
  const appState = useAppState();
  const experimentId = useExperimentId();
  const vertexParams = useVertexPersonalizationParams();
  const { data: plpCMSData } = useContentStackPLPData({
    enabled: true,
  });
  const retryConfig = useRetryConfigForCategory('SEARCH');

  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = isServer ? appState.state.reqUrl : legacyRouter.asPath;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }

  const { data: pageTypeData } = usePageType();
  const parsedUrl = parseUrl(reqUrl);

  const lwTag = plpCMSData?.additional_attributes_merch_shelf_page?.lucidworks_fusion_tag
    ? plpCMSData.additional_attributes_merch_shelf_page.lucidworks_fusion_tag
    : undefined;

  const taxonomyPath = pageTypeData?.taxonomyPath || undefined;
  delete parsedUrl.query.loadMore;
  const cleanedReqUrl = stringifyUrl(parsedUrl);
  const { data: headerData } = useHeaderData();
  const { acesEnabled } = useAcesFlags();
  const vehicleQuestions = useMemo(
    () =>
      extractVehicleQuestions(
        headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
        true,
      ) as string,
    [headerData?.vehicleMap],
  );

  // Validate vehicle parameters - if any base param is missing, exclude ALL vehicle params
  const validatedVehicleParams = validateAndFilterVehicleParams({
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    vehicleQuestions: vehicleQuestions || undefined,
  });

  const normalizedExperimentId = getExperimentId(experimentId, true);

  const queryOptions: Options = {
    rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
    eCookieId: appState.state.eCookieId,
    locale,
    partGroupId: pageTypeData?.catalogRepositoryId || '',
    pageType: pageTypeData?.pageType || undefined,
    canonicalPath: pageTypeData?.canonicalPath || parsedUrl.url,
    taxonomyPath,
    tag: lwTag,
    botEnabledFacetPath: pageTypeData?.botEnabledFacetPath || undefined,
    makeModelYearPath: pageTypeData?.makeModelYearPath || undefined,
    recsPerPage: '24',
    storeId: getShelfRequestStoreId(
      headerResult.data?.storeNumber,
      isBot,
      pageTypeData?.botEnabledFacetPath,
    ),
    path: legacyRouter.route === '/cms/taxonomy-selector' ? taxonomyPath : cleanedReqUrl,
    preview: showXMPreviewDate(),
    ...rest,
    ignoreVehicleSpecificProductsCheck:
      typeof parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'string'
        ? parsedUrl.query.ignoreVehicleSpecificProductsCheck === 'true'
        : undefined,
    ...validatedVehicleParams,
    ...(normalizedExperimentId && { experimentId: normalizedExperimentId }),
    ...(vertexParams.shouldInjectParams && {
      visitorId: vertexParams.visitorId,
      userId: vertexParams.userId,
    }),
  };

  const infiniteQueryResult = useACESProductShelfResultsData({
    queryOptions,
    enabled:
      enabled &&
      vertexParams.isMyProfileReady &&
      pageTypeData?.pageType === 'ProductShelf' &&
      (legacyRouter.route === '/[...seoUrlPath]' ||
        legacyRouter.route === '/cms/taxonomy-selector') &&
      acesEnabled,
    staleTime: 30 * 60000,
    ...retryConfig,
    retryOnMount: false,
  });

  useQueryResultOnError(infiniteQueryResult, (err) => {
    logger.error({
      message: 'Error fetching product shelf results',
      meta: {
        error: isAxiosError<unknown>(err) ? err.response?.data : err,
      },
    });

    try {
      router.replace('/errorPage');
    } catch {
      throw new Error(`There was a problem redirecting to /errorPage`);
    }
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    isFetchingNextPage,
  } = infiniteQueryResult;

  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  return {
    pages,
    data: mergedData,
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    isFetchingNextPage,
  };
};

export const prefetchProductShelfACESResults = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Options,
) => {
  return prefetchACESProductShelfResultsData(queryClient, options, axiosInstance);
};

export const getProductShelfACESResultsFromCache = (queryClient: QueryClient, options: Options) => {
  return getFromCache(queryClient, options);
};

export const useProductShelfACESFacets = () => {
  const router = useLegacyRouter();
  const headerResult = useHeaderData();
  const isBot = useDeviceType() === 'bot';
  const locale = useLocale();
  const appState = useAppState();

  // TODO: replace with router.asPath when Next bug is fixed
  // https://github.com/vercel/next.js/issues/46876
  const reqUrl = isServer ? appState.state.reqUrl : router.asPath;

  if (reqUrl == null) {
    throw new Error(`A 'path' option is required`);
  }

  const { data: pageTypeData } = usePageType();

  const commonOptions = {
    eCookieId: appState.state.eCookieId,
    rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
    locale,
    vehicleId: undefined,
    pageType: pageTypeData?.pageType ?? 'ProductShelf',
    canonicalPath: pageTypeData?.canonicalPath ?? parseUrl(reqUrl).url,
    taxonomyPath: pageTypeData?.taxonomyPath,
    botEnabledFacetPath: undefined,
    makeModelYearPath: undefined,
    recsPerPage: '1',
    storeId: getShelfRequestStoreId(
      headerResult.data?.storeNumber,
      isBot,
      pageTypeData?.botEnabledFacetPath,
    ),
    path: reqUrl,
    preview: showXMPreviewDate(),
    staleTime: 30 * 60000,
    retry: 1,
    retryOnMount: false,
  };

  const queryResult = useACESProductShelfResultsData({
    queryOptions: {
      ...commonOptions,
      partGroupId: pageTypeData?.catalogRepositoryId || '',
    },
  });

  const { data, isPending, isSuccess, isFetching } = queryResult;

  return {
    data: data?.pages[0],
    isFetching,
    isPending,
    isSuccess,
  };
};

export { productsShelfACESQuery, useACESProductShelfResultsData };
